import { Link } from "react-router-dom";
import { ArrowRight, Globe2, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine, Overline, SectionHeading } from "../components/Reveal";
import { AbstractVisual, ParticleField } from "../components/AbstractVisual";

const PATHS = [
  { icon: ArrowDownToLine, title: "Global → India", desc: "We bring internationally successful VR/XR experiences to malls, museums, entertainment centres, tourist destinations, theme parks and dedicated immersive venues across India — handling licensing, localization, deployment and operations.", testid: "distribution-global-to-india" },
  { icon: ArrowUpFromLine, title: "India → Global", desc: "We help Indian XR creators distribute their experiences internationally, building pathways to venues and partners across Europe, North America, Asia and the Middle East.", testid: "distribution-india-to-global" },
  { icon: Globe2, title: "Licensing & Operations", desc: "From single-experience licensing to full venue programming — content curation, technology deployment, equipment management, ticketing, marketing and customer support.", testid: "distribution-licensing" },
];

export default function Distribution() {
  return (
    <main>
      <Seo title="Global XR Distribution" path="/distribution"
        description="VR content distribution India — we license and distribute premium immersive experiences across India and global markets, and help global XR creators enter India." />
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <AbstractVisual hue={38} seed={33} />
        <ParticleField hue={38} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-[#030305]/40 to-[#030305]/70" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pb-24 w-full">
          <Overline className="mb-6">Global XR Distribution</Overline>
          <h1 className="font-serif tracking-tight leading-[1.0] text-5xl md:text-7xl lg:text-8xl" data-testid="distribution-headline">
            <MaskedLine delay={0.25}>World-Class XR.</MaskedLine>
            <MaskedLine delay={0.4}><span className="italic text-zinc-400">New Markets.</span></MaskedLine>
          </h1>
          <p className="mt-8 max-w-xl text-zinc-400 font-light leading-relaxed">
            We partner with international VR/XR studios and experience creators to license and distribute premium immersive experiences across India and other markets.
          </p>
        </div>
      </section>

      <section className="py-28 md:py-36 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PATHS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div data-testid={p.testid} className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-10 h-full hover:border-white/25 transition-colors duration-500">
                <p.icon size={22} className="text-zinc-400 mb-8" />
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-4">{p.title}</h3>
                <p className="text-sm text-zinc-400 font-light leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-28 md:py-40 border-t border-white/[0.06] text-center relative overflow-hidden">
        <ParticleField hue={38} count={14} />
        <div className="relative max-w-3xl mx-auto px-6">
          <Reveal>
            <SectionHeading overline="Partner With Vrova" title={<>Have an immersive experience? <span className="italic text-zinc-400">We'll help you take it further.</span></>} className="mx-auto text-center" />
            <div className="mt-12 flex flex-wrap justify-center gap-4">
              <Link to="/creators" data-testid="distribution-creator-cta"
                className="px-9 py-4 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors inline-flex items-center gap-2">
                Become a Distribution Partner <ArrowRight size={15} />
              </Link>
              <Link to="/venues" data-testid="distribution-venue-cta"
                className="px-9 py-4 rounded-full border border-white/20 text-sm hover:bg-white/10 transition-colors">
                License for Your Venue
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
